/**
 * JuntaPanel — Consultas para la junta de aclaraciones (RFI).
 *
 * Estados: idle (CTA para generar) · running (spinner + polling) · completed
 * (los dos documentos: escrito y anexo) · failed (aviso + reintentar).
 *
 * El estado autoritativo sale del GET `/api/v1/projects/:id/junta`. Al generar
 * se hace polling de ese GET cada 4 s hasta completed/failed — más simple y
 * robusto que el WS para esta v1. La descarga aún no está: botón deshabilitado.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  TabList,
  Tab,
  Toast,
  ToastTitle,
  Tooltip,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components';
import {
  ArrowDownload24Regular,
  ArrowSync24Regular,
  DocumentQuestionMark24Regular,
  Sparkle24Regular,
} from '@fluentui/react-icons';
import { refreshUsageSoon } from '../stores/usageStore.js';
import { downloadJunta, generateJunta, getJunta } from '../api/junta.js';
import { GLOBAL_TOASTER_ID } from './ThemedProvider.js';
import { PlanosNotice } from './PlanosNotice.js';
import { DWG_DXF_ENABLED } from '../lib/flags.js';
import type { Project } from '../api/projects.js';
import type {
  JuntaConsulta,
  JuntaEstado,
  JuntaPrioridad,
  JuntaResultado,
} from '@supernova/shared';

interface Props {
  projectId: string;
  project: Project;
  /** El proyecto aún procesa documentos (parseo/indexación): no se puede generar. */
  isProcessing: boolean;
  /** Falso en el proyecto de ejemplo o sin permiso de escritura. */
  canMutate: boolean;
  /** Láminas de planos que faltan por leer (para el aviso de planos). */
  planosPendientes?: number;
  /** Costo estimado de leer los planos que faltan (solo staff Ecosoft). */
  planosCostoUsd?: number | null;
}

const POLL_MS = 4000;

// El color de la prioridad usa los tokens de semáforo del design system:
// alta → peligro (--bad), media → ámbar (--warn), baja → neutro. Nada de hex.
const PRIO: Record<JuntaPrioridad, { label: string; fg: string; bg: string }> = {
  alta: { label: 'Alta', fg: 'var(--bad)', bg: 'var(--bad-weak)' },
  media: { label: 'Media', fg: 'var(--warn)', bg: 'var(--warn-weak)' },
  baja: { label: 'Baja', fg: 'var(--text-tertiary)', bg: 'var(--bg-surface-alt, var(--bg-elev))' },
};

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: 'var(--space-l)' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-s)',
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-xs)',
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  actions: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  meta: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    display: 'flex',
    gap: '14px',
    flexWrap: 'wrap',
  },
  metaStrong: { color: 'var(--text-primary)', fontWeight: 700 },
  // ---- estado vacío ----
  empty: {
    border: '1px dashed var(--border-subtle)',
    borderRadius: 'var(--radius-l)',
    padding: 'var(--space-xl)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
    alignItems: 'center',
  },
  emptyOrb: {
    width: '56px',
    height: '56px',
    borderRadius: 'var(--radius-l)',
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    display: 'grid',
    placeItems: 'center',
  },
  emptyTitle: { fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' },
  emptyText: { maxWidth: '54ch', color: 'var(--text-secondary)', fontSize: 'var(--fs-300)', lineHeight: 1.5 },
  idleNote: {
    maxWidth: '54ch',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--fs-200)',
    lineHeight: 1.45,
  },
  hint: {
    display: 'flex',
    gap: '9px',
    alignItems: 'flex-start',
    background: 'var(--oficial-weak)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-m)',
    padding: '9px 13px',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    maxWidth: '58ch',
    textAlign: 'left',
  },
  warnLine: { fontSize: 'var(--fs-200)', color: 'var(--warn)', textAlign: 'center' },
  planosNote: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    paddingLeft: 'var(--space-xs)',
  },
  // ---- running ----
  running: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-s)',
    padding: 'var(--space-l)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
  },
  // ---- error ----
  err: {
    color: 'var(--semantic-danger, var(--bad))',
    fontSize: 'var(--fs-200)',
    background: 'var(--bad-weak)',
    padding: 'var(--space-s) var(--space-m)',
    borderRadius: 'var(--radius-s)',
  },
  // ---- segmented / documentos ----
  segNote: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  pill: {
    fontSize: 'var(--fs-100)',
    fontWeight: 650,
    padding: '2px 8px',
    borderRadius: 'var(--radius-pill, 999px)',
  },
  pillPresent: { background: 'var(--ok)', color: tokens.colorNeutralForegroundOnBrand },
  list: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'var(--space-s)' },
  // ---- tarjeta de consulta ----
  q: {
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-m)',
    background: 'var(--bg-elev)',
    padding: '13px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    alignSelf: 'flex-start',
    fontSize: '10.5px',
    fontWeight: 750,
    letterSpacing: '0.02em',
    padding: '3px 9px',
    borderRadius: 'var(--radius-pill, 999px)',
    textTransform: 'uppercase',
  },
  chipDot: { width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' },
  cite: {
    fontSize: '11.5px',
    color: 'var(--text-secondary)',
    borderLeft: '2px solid var(--border-strong, var(--border-subtle))',
    paddingLeft: '9px',
    lineHeight: 1.45,
  },
  citeLoc: {
    display: 'block',
    fontFamily: 'var(--font-mono)',
    fontSize: '10.5px',
    color: 'var(--text-tertiary)',
    fontWeight: 750,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: '3px',
  },
  ask: { fontSize: '13.5px', color: 'var(--text-primary)', lineHeight: 1.5 },
  askLab: {
    fontFamily: 'var(--font-mono)',
    fontSize: '9.5px',
    color: 'var(--text-tertiary)',
    fontWeight: 750,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginRight: '6px',
  },
  rubroCount: { color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontWeight: 700 },
});

interface JuntaState {
  status: JuntaEstado;
  result: JuntaResultado | null;
  error: string | null;
  generatedAt: string | null;
}

function fmtFecha(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function JuntaPanel({
  projectId,
  project,
  isProcessing,
  canMutate,
  planosPendientes,
  planosCostoUsd,
}: Props) {
  const styles = useStyles();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const [state, setState] = useState<JuntaState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [doc, setDoc] = useState<'escrito' | 'anexo'>('escrito');
  const [confirmRegen, setConfirmRegen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const j = await getJunta(projectId);
      setState({ status: j.status, result: j.result, error: j.error, generatedAt: j.generatedAt });
      setErr(null);
      return j.status;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo cargar');
      return null;
    }
  }, [projectId]);

  // Polling del GET mientras el job corre. Se rearma solo hasta completed/failed.
  const poll = useCallback(() => {
    stopPolling();
    pollRef.current = setTimeout(async () => {
      const st = await load();
      if (st === 'running') {
        poll();
      } else {
        if (st === 'completed') refreshUsageSoon(); // consumió tokens → refrescar pill
      }
    }, POLL_MS);
  }, [load, stopPolling]);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      const st = await load();
      setLoading(false);
      if (st === 'running') poll();
    })();
    return stopPolling;
  }, [load, poll, stopPolling]);

  async function onGenerate() {
    setBusy(true);
    setErr(null);
    try {
      await generateJunta(projectId);
      setState((prev) => ({
        status: 'running',
        result: prev?.result ?? null,
        error: null,
        generatedAt: prev?.generatedAt ?? null,
      }));
      poll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo generar');
    } finally {
      setBusy(false);
    }
  }

  // 'ambos' no es un documento del backend: baja el escrito y luego el anexo
  // como dos archivos. La pausa breve evita que el navegador bloquee la 2ª
  // descarga por venir demasiado pegada a la 1ª.
  async function onDownload(opcion: 'escrito' | 'anexo' | 'ambos') {
    setDescargando(true);
    try {
      if (opcion === 'ambos') {
        await downloadJunta(projectId, 'escrito');
        await new Promise((r) => setTimeout(r, 300));
        await downloadJunta(projectId, 'anexo');
      } else {
        await downloadJunta(projectId, opcion);
      }
    } catch (e) {
      dispatchToast(
        <Toast>
          <ToastTitle>
            {e instanceof Error ? e.message : 'No se pudo descargar el documento.'}
          </ToastTitle>
        </Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setDescargando(false);
    }
  }

  const status = state?.status ?? 'idle';
  const result = state?.result ?? null;
  const isRunning = status === 'running';
  const hasResult = status === 'completed' && result != null;
  const sinArchivos = (project.chunksTotal ?? 0) === 0 && !isProcessing;
  const bloqueado = isProcessing || sinArchivos;
  const blockMsg = isProcessing
    ? 'Espera a que terminen de leerse los documentos'
    : sinArchivos
      ? 'Sube el pliego para poder generar las consultas'
      : '';

  const tienePlanos = (project.planosLaminas ?? 0) > 0;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <span className={styles.title}>
          <DocumentQuestionMark24Regular fontSize={14} /> Consultas para la junta de aclaraciones
        </span>
        {hasResult && (
          <div className={styles.actions}>
            <Tooltip content={blockMsg} relationship="label" visible={blockMsg ? undefined : false}>
              <Button
                appearance="secondary"
                icon={busy || isRunning ? <Spinner size="extra-tiny" /> : <ArrowSync24Regular />}
                size="small"
                disabled={busy || isRunning || !canMutate || bloqueado}
                onClick={() => (canMutate ? setConfirmRegen(true) : void onGenerate())}
              >
                Regenerar
              </Button>
            </Tooltip>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="primary"
                  icon={descargando ? <Spinner size="extra-tiny" /> : <ArrowDownload24Regular />}
                  size="small"
                  disabled={descargando}
                >
                  Descargar
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => void onDownload('escrito')}>
                    Escrito de consultas
                  </MenuItem>
                  <MenuItem onClick={() => void onDownload('anexo')}>
                    Consultas de uso interno
                  </MenuItem>
                  <MenuItem onClick={() => void onDownload('ambos')}>
                    Ambos (dos archivos)
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        )}
      </div>

      {err && <div className={styles.err}>{err}</div>}

      {/* ===== idle / vacío ===== */}
      {!loading && !isRunning && !hasResult && (
        <>
          {tienePlanos && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <PlanosNotice
                project={project}
                pendientes={planosPendientes}
                costoUsd={planosCostoUsd}
                canEdit={canMutate}
              />
              <span className={styles.planosNote}>
                {DWG_DXF_ENABLED
                  ? 'Los planos se leen de un PDF, DWG o DXF. Una foto suelta (JPG o PNG) no se lee.'
                  : 'Por ahora los planos solo se leen dentro de un PDF. Una foto suelta (JPG o PNG) no se lee.'}
              </span>
            </div>
          )}

          {status === 'failed' && state?.error && (
            <div className={styles.err}>{state.error}</div>
          )}

          <div className={styles.empty}>
            <div className={styles.emptyOrb}>
              <DocumentQuestionMark24Regular fontSize={28} />
            </div>
            <div className={styles.emptyTitle}>
              Genera las consultas para la junta de aclaraciones
            </div>
            <p className={styles.emptyText}>
              OPUS ai lee el pliego completo y arma dos documentos: un <strong>escrito de
              consultas</strong> para presentar a la convocante y unas{' '}
              <strong>consultas de uso interno</strong> para el equipo de estudios. La junta ocurre
              antes de cotizar.
            </p>
            <p className={styles.idleNote}>
              Cuantos más documentos del concurso subas —bases, especificaciones y planos, no solo
              el catálogo— más completas serán las consultas.
            </p>
            <Tooltip content={blockMsg} relationship="label" visible={blockMsg ? undefined : false}>
              <Button
                appearance="primary"
                icon={busy ? <Spinner size="extra-tiny" /> : <Sparkle24Regular />}
                disabled={busy || bloqueado || !canMutate}
                onClick={() => void onGenerate()}
              >
                {status === 'failed' ? 'Volver a generar' : 'Generar consultas'}
              </Button>
            </Tooltip>
            {bloqueado && <span className={styles.warnLine}>{blockMsg}</span>}
            <div className={styles.hint}>
              <Sparkle24Regular fontSize={16} style={{ flexShrink: 0, color: 'var(--accent)' }} />
              <span>
                El pliego se lee una vez. En un documento de ~150 páginas toma un par de minutos.
                Podrás regenerar cuando cambien los documentos.
              </span>
            </div>
          </div>
        </>
      )}

      {/* ===== running ===== */}
      {isRunning && (
        <div className={styles.running}>
          <Spinner size="tiny" />
          <span>Leyendo el pliego y armando las consultas…</span>
        </div>
      )}

      {/* ===== completed ===== */}
      {hasResult && (
        <>
          <div className={styles.meta}>
            {state?.generatedAt && (
              <span>
                Generado el <span className={styles.metaStrong}>{fmtFecha(state.generatedAt)}</span>
              </span>
            )}
            <span>
              <span className={styles.metaStrong}>{result.escrito.length}</span> en el escrito ·{' '}
              <span className={styles.metaStrong}>{result.anexo.length}</span> de uso interno
            </span>
          </div>

          <TabList
            selectedValue={doc}
            onTabSelect={(_, d) => setDoc(d.value as 'escrito' | 'anexo')}
          >
            <Tab value="escrito">Escrito de consultas ({result.escrito.length})</Tab>
            <Tab value="anexo">Consultas de uso interno ({result.anexo.length})</Tab>
          </TabList>

          {doc === 'escrito' && (
            <div>
              <div className={styles.segNote}>
                <span className={`${styles.pill} ${styles.pillPresent}`}>Se presenta</span>
                <span>El documento que entregas a la convocante. Ordenado por prioridad.</span>
              </div>
              <div className={styles.list}>
                {result.escrito.map((q, i) => (
                  <ConsultaCard key={i} q={q} styles={styles} />
                ))}
                {result.escrito.length === 0 && (
                  <div className={styles.emptyText}>No hay consultas para presentar.</div>
                )}
              </div>
            </div>
          )}

          {doc === 'anexo' && (
            <div>
              <div className={styles.segNote}>
                <span>
                  Repaso del equipo de estudios: lo que hay que definir antes de poner precios,
                  agrupado por rubro de obra.
                </span>
              </div>
              <AnexoPorRubro anexo={result.anexo} styles={styles} />
            </div>
          )}
        </>
      )}

      {loading && (
        <div className={styles.running}>
          <Spinner size="tiny" />
          <span>Cargando…</span>
        </div>
      )}

      {/* Confirmación de regenerar (solo cuando el usuario puede mutar). */}
      <Dialog open={confirmRegen} onOpenChange={(_, d) => setConfirmRegen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Regenerar las consultas</DialogTitle>
            <DialogContent>
              Se vuelve a leer el pliego completo y se arman de nuevo los dos documentos. Las
              consultas actuales se reemplazan. Conviene hacerlo cuando cambiaron los documentos del
              concurso.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmRegen(false)}>
                Cancelar
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmRegen(false);
                  void onGenerate();
                }}
              >
                Regenerar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

type Styles = ReturnType<typeof useStyles>;

function ConsultaCard({ q, styles }: { q: JuntaConsulta; styles: Styles }) {
  const prio = PRIO[q.prioridad ?? 'media'];
  const cita = q.citas?.[0] ?? q.cita;
  return (
    <div className={styles.q}>
      <span className={styles.chip} style={{ color: prio.fg, background: prio.bg }}>
        <span className={styles.chipDot} />
        {prio.label}
      </span>
      <div className={styles.cite}>
        <span className={styles.citeLoc}>Dice el pliego · pág. {q.pagina}</span>“{cita}”
      </div>
      <div className={styles.ask}>
        <span className={styles.askLab}>Consulta</span>
        {q.pregunta}
      </div>
    </div>
  );
}

function AnexoPorRubro({ anexo, styles }: { anexo: JuntaConsulta[]; styles: Styles }) {
  // Agrupar por rubro (fallback "Otros"). Se conserva el orden de aparición.
  const grupos: { rubro: string; items: JuntaConsulta[] }[] = [];
  const idx = new Map<string, number>();
  for (const q of anexo) {
    const r = q.rubro && q.rubro.trim() ? q.rubro : 'Otros';
    let g = idx.get(r);
    if (g === undefined) {
      idx.set(r, grupos.length);
      grupos.push({ rubro: r, items: [q] });
    } else {
      grupos[g]!.items.push(q);
    }
  }

  const primero = grupos[0];
  if (!primero) {
    return <div className={styles.list}><div className={styles.emptyText}>No hay consultas de uso interno.</div></div>;
  }

  return (
    <Accordion multiple collapsible defaultOpenItems={[primero.rubro]} className={styles.list}>
      {grupos.map((g) => (
        <AccordionItem key={g.rubro} value={g.rubro}>
          <AccordionHeader>
            <span>
              {g.rubro} <span className={styles.rubroCount}>({g.items.length})</span>
            </span>
          </AccordionHeader>
          <AccordionPanel>
            <div className={styles.list}>
              {g.items.map((q, i) => (
                <ConsultaCard key={i} q={q} styles={styles} />
              ))}
            </div>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
